<template>
  <div class="container">
    <button id="first-page" class="page-icon" @click="onClickFirstPage" :disabled="firstPageSelected">
      <span class="material-symbols-outlined size-24 arrow">
        keyboard_double_arrow_left
      </span>
    </button>
    <button id="previous-page" class="page-icon" @click="onClickPreviousPage" :disabled="firstPageSelected">
      <span class="material-symbols-outlined size-24 arrow">
        keyboard_arrow_left
      </span>
    </button>
    <div class="pages">
      <div v-for="(page, index) in pageIconCount" :key="page.id" class="page-div">
        <span class="page" :class="{ 'active-page': index + offset + 1 === currentPage }"
          @click="onSelectPageNumber(index)">{{ index + offset + 1 }}</span>
      </div>
    </div>
    <button id="next-page" class="page-icon" @click="onClickNextPage" :disabled="lastPageSelected">
      <span class="material-symbols-outlined size-24 arrow">
        keyboard_arrow_right
      </span>
    </button>
    <button id="last-page" class="page-icon" @click="onClickLastPage" :disabled="lastPageSelected">
      <span class="material-symbols-outlined size-24 arrow">
        keyboard_double_arrow_right
      </span>
    </button>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

//based on wireframes
const MAX_PAGES = 5
const MAX_CARDS = 6

const props = defineProps({
  distance: Number,
  cardList: Array,
  setPageOne: Boolean,
})

const emit = defineEmits(['onCurrentPageChange'])

const currentPage = ref(1)
const offset = ref(0)
const firstPageSelected = computed(() => currentPage.value === 1)
const totalPages = computed(() => Math.ceil(props.cardList.length / MAX_CARDS))
const lastPageSelected = computed(() => currentPage.value === totalPages.value)
const genomicsResults = computed(() => store.getters.session?.genomicsResults || {})

const pageIconCount = computed(() => {
  const nearestMultiple = Math.trunc(totalPages.value / MAX_PAGES) * MAX_PAGES
  if (currentPage.value > nearestMultiple) {
    return totalPages.value - nearestMultiple
  }
  return Math.min(MAX_PAGES, totalPages.value)
})

const emitCurrentPage = () => {
  emit('onCurrentPageChange', currentPage.value)
}

//zero indexed
const onSelectPageNumber = (index) => {
  const pageNumber = index + offset.value + 1
  currentPage.value = pageNumber
  emitCurrentPage()
}

const calculateOffset = () => {
  offset.value = Math.trunc((currentPage.value - 1) / MAX_PAGES) * MAX_PAGES
}

const onClickNextPage = () => {
  if (currentPage.value >= totalPages.value) { return }
  currentPage.value += 1
  calculateOffset()
  emitCurrentPage()
}

const onClickPreviousPage = () => {
  if (currentPage.value <= 1) { return }
  currentPage.value -= 1
  calculateOffset()
  emitCurrentPage()
}

const onClickFirstPage = () => {
  currentPage.value = 1
  offset.value = 0
  emitCurrentPage()
}

const onClickLastPage = () => {
  currentPage.value = totalPages.value
  calculateOffset()
  emitCurrentPage()
}

watch(genomicsResults, () => {
  currentPage.value = 1
  offset.value = 0
})

watch(() => props.setPageOne, () => {
  if (props.setPageOne) {
    currentPage.value = 1
    offset.value = 0
  }
})
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  gap: calc(3vw - 4.7rem);
}

.page-icon {
  margin: 0 max(0.2rem, calc(1vw - 1rem));
  user-select: none;
  border: none;
  border-radius: 6px;
  color: var(--Grey600);

  cursor: pointer;

  &:hover {
    color: var(--Black);
  }
}

.page-div {
  width: calc(3vw - 1rem);
}

.pages {
  display: flex;
  flex-direction: row;
  flex-shrink: 1;
  margin-right: 0.5rem;
}

.page {
  padding: 7px;
  background-color: var(--Grey100);
  margin: 0 7px;
  font-size: 80%;
  border-radius: 6px;
  user-select: none;

  cursor: pointer;

  &:hover {
    color: var(--Black);
    background-color: var(--Grey300);
  }
}

.active-page {
  color: var(--White);
  background-color: var(--Primary);

  &:hover {
    color: var(--White);
    background-color: var(--Primary);
  }
}

.arrow {
  margin-top: 4px;
}

*:disabled {
  cursor: default;
  color: var(--Grey300);

  &:hover {
    color: var(--Grey200);
  }
}
</style>
